import { memo, useContext, useRef, useState } from 'react'
import { alpha, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material'
import { ExtraSmallButton } from '../../../custom'
import { AppContext } from '../../../../App'
import { useSelector } from 'react-redux'
import { selectActiveChartId } from '../../../../selectors/chart'
import '../styles.css'
import { selectMultiPaneFullScreen } from '../../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../../assets/constants'
import * as indicatorProto from '../../../../proto/generated/indicator'
import { RootState } from '../../../../store'
import OI from '../../../../assets/icons/OI'
import { DrawingToolMenuItem } from './drawingToolMenu'
import ThinArrowUpIcon from '../../../../assets/icons/ThinArrowUpIcon'

export type OiDrawingToolMenuProps = {
    items: DrawingToolMenuItem[]
    disable: boolean
}

const OiDrawingToolMenu: React.FC<OiDrawingToolMenuProps> = ({ items, disable }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const indicators: indicatorProto.Indicator[] = useSelector((state: RootState) => state.chartStore.indicators)
    const buttonRef = useRef<HTMLButtonElement | null>(null)

    const handleClick = () => {
        setAnchorEl(buttonRef.current)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const container = useSelector(selectMultiPaneFullScreen)

    const onIndicatorSelected = (indicator: indicatorProto.Indicator) => {
        if (activeChartId && appContext.chart && appContext.chart[activeChartId]) {
            appContext.chart[activeChartId]?.addIndicator(indicator)
        }
    }

    return (
        <Stack
            alignItems={'center'}
            sx={{
                position: 'relative',
                '&:hover .drawing-options-menu': {
                    visibility: 'visible !important'
                }
            }}
        >
            <ExtraSmallButton
                onClick={handleClick}
                ref={buttonRef}
                color="inherit"
                sx={{
                    visibility: Boolean(anchorEl) ? 'visible' : 'hidden',
                    bgcolor: theme => Boolean(anchorEl) ? alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity) : 'transparent',
                    position: 'absolute',
                    top: '-3px',
                    height: '10px',
                    p: 0,
                    margin: 0,
                    width: '32px',
                    zIndex: 1,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0
                }}
                className="drawing-options-menu"
                data-event={'OI menu clicked from toolbar'}
            >
                <ThinArrowUpIcon sx={{fontSize: '12px'}} />
            </ExtraSmallButton>
            <ExtraSmallButton
                id={'oi-indicator-menu-button'}
                color={'inherit'}
                onClick={handleClick}
                sx={{ p: 0, mt: '4px', mx: '2px' }}
                disabled={disable}
                data-event={'OI menu clicked from toolbar'}
            >
                <OI />
            </ExtraSmallButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                elevation={3}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
            >
                {items.map((i) => (
                    <MenuItem
                        key={`split-horizontal-${i.text}`}
                        onClick={() => {
                            const indicator = indicators.find((indicator) => indicator.code === i.code)
                            if (indicator) {
                                onIndicatorSelected(indicator)
                            }
                            handleClose()
                        }}
                        data-event={`Selected ${i.text} from OI menu`}
                    >
                        <ListItemIcon sx={{ marginRight: '5px' }}>{i.icon}</ListItemIcon>
                        <ListItemText
                            sx={{ fontSize: '12px' }}>{i.text === 'OI Historical Profile' ? 'OI Heatmap' : i.text}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </Stack>
    )
}

export default memo(OiDrawingToolMenu)
